import type { LocalDate } from '@tahi/core';
import { copy } from '../copy';
import { weekdayShort } from '../format';

const HEIGHT = 96;

/** Daily counts as columns, each with its number and a dashed tick at that day's limit. No color-only meaning. */
export function CountChart({
  days,
  today,
}: {
  days: { date: LocalDate; count: number; limit: number | null }[];
  today: LocalDate;
}) {
  const max = Math.max(1, ...days.map((d) => Math.max(d.count, d.limit ?? 0)));
  const label = (d: LocalDate) => (d === today ? 'Today' : weekdayShort(d));
  return (
    <div role="group" aria-label={copy.week}>
      <div className="flex items-end gap-1.5" style={{ height: HEIGHT + 18 }}>
        {days.map((d) => {
          const h = Math.max(2, Math.round((d.count / max) * HEIGHT));
          const tick = d.limit === null ? null : Math.round((d.limit / max) * HEIGHT);
          const name = `${label(d.date)}, ${d.count}${d.limit === null ? '' : `, limit ${d.limit}`}`;
          return (
            <div
              key={d.date}
              role="img"
              aria-label={name}
              className="relative flex h-full min-w-0 flex-1 flex-col items-center justify-end"
            >
              <span aria-hidden className="mb-1 font-mono text-xs tabular-nums text-muted">
                {d.count}
              </span>
              <div
                aria-hidden
                className="w-full rounded"
                style={{ height: h, background: 'var(--chart-1)' }}
              />
              {tick !== null ? (
                <div
                  aria-hidden
                  className="absolute inset-x-0 border-t-2 border-dashed border-border"
                  style={{ bottom: tick }}
                />
              ) : null}
            </div>
          );
        })}
      </div>
      <div aria-hidden className="mt-1 flex gap-1.5">
        {days.map((d) => (
          <span key={d.date} className="min-w-0 flex-1 truncate text-center text-xs text-muted">
            {label(d.date)}
          </span>
        ))}
      </div>
    </div>
  );
}
